import React from 'react'
import { Button } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import { DayOffLeaderApprove } from '@/modules/Dayoffs/types/payload'
import { approveLeadDayOff } from '@/modules/Dayoffs/services'
import toast from 'react-hot-toast'
import { IPermissionProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { leaderApproveSchema } from '../LeaderApproveForm/yup'
import { FormItem } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { useRefetchDayOffQueries } from '@/modules/Dayoffs/constants'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
interface DayOffFormLeaderApproveAllProps extends IPermissionProps {
    ids: number[]
    onClose?: () => void
    refetch: () => void
}
const LeaderFormApproveAll: React.FC<DayOffFormLeaderApproveAllProps> = ({
    ids,
    onClose,
    refetch,
}) => {
    const { t: translate } = useTranslation('dayoffs')
    const { refetchAll } = useRefetchDayOffQueries()
    const { mutateAsync: LeaderConfirm, isPending: isLeaderConfirming } =
        useMutationWithHandler(
            {
                mutationFn: approveLeadDayOff,
            },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: translate('something_went_wrong'),
            }
        )

    const { control: controlLeaderForm, handleSubmit: handleLeaderConfirm } =
        useForm<DayOffLeaderApprove>({
            defaultValues: {
                day_off_feedback_lead: translate('approved'),
            },
            resolver: yupResolver(leaderApproveSchema),
        })

    const onLeaderSubmit = async (dataReq: DayOffLeaderApprove) => {
        if (dataReq?.day_off_feedback_lead?.trim() === '') {
            toast.error(translate('please_feedback'))
            return
        }
        try {
            const promises = ids.map((id) =>
                LeaderConfirm({
                    id: id || 0,
                    feedback: {
                        day_off_feedback_lead: dataReq?.day_off_feedback_lead,
                    },
                })
            )
            await Promise.allSettled(promises)
            toast.success(translate('bulk_approve_success'))
            if (onClose) onClose()
            refetch()
            refetchAll()
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <div className="w-full bg-white rounded-lg border border-gray-200 p-4 text-sm h-full">
            <form
                onSubmit={handleLeaderConfirm(onLeaderSubmit)}
                className="flex flex-col h-full"
            >
                <FormItem
                    control={controlLeaderForm}
                    name="day_off_feedback_lead"
                    type="textarea"
                    placeholder={translate('leader_feedback_placeholder')}
                    disabled={isLeaderConfirming || ids.length === 0}
                    label={translate('feedback')}
                />
                <div className="w-full flex justify-end gap-2 mt-auto pt-2">
                    {onClose && (
                        <Button
                            size="small"
                            type="button"
                            onClick={onClose}
                            variant="outlined"
                        >
                            {translate('close')}
                        </Button>
                    )}
                    <Button
                        loading={isLeaderConfirming}
                        type="submit"
                        size="small"
                        disabled={isLeaderConfirming || ids.length === 0}
                    >
                        {translate('confirm')}{' '}
                        {ids.length > 0 && `(${ids.length})`}
                    </Button>
                </div>
            </form>
        </div>
    )
}
export const LeaderApproveAllForm = (
    props: DayOffFormLeaderApproveAllProps
) => {
    return <LeaderFormApproveAll {...props} />
}
